import { Link } from "react-router-dom";
import {
  ArrowRight,
  Target,
  Eye,
  GraduationCap,
  Users,
  HeartPulse,
  Wallet,
  HandHeart,
  Briefcase,
  ShieldCheck,
  Sprout,
} from "lucide-react";
import { ORG } from "@/lib/org";

/* ---------------- Mission Statement ---------------- */
export function Mission() {
  return (
    <section id="mission" data-testid="mission-section" className="section bg-[var(--brand-cream)] text-black">
      <div className="container-x grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="eyebrow mb-4">Our Mission</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-black">
            A Safe Place <br /> To <span className="brand-red">Become.</span>
            <br /> A Strong Place <br /> To <span className="brand-red">Belong.</span>
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-6">
          <blockquote
            data-testid="mission-statement"
            className="border-l-4 border-[var(--brand-red)] pl-6 text-lg md:text-2xl font-display font-bold uppercase leading-tight text-black"
          >
            RE//FORM PLACE CHI exists to reach souls, root disciples, release
            destiny, strengthen families, and reform culture — creating
            environments where individuals, families, and communities experience
            meaningful and sustainable transformation.
          </blockquote>
          <div className="grid md:grid-cols-2 gap-px bg-black/10">
            {[
              {
                icon: Target,
                t: "Mission",
                d: "To develop healthier, equipped, and connected people through spiritual formation, community development, leadership cultivation, and whole-person transformation.",
              },
              {
                icon: Eye,
                t: "Vision",
                d: "Stronger Chicago communities — where barriers are removed, access to resources is expanded, families are strengthened, and every person has a pathway to thrive.",
              },
            ].map((x) => {
              const Icon = x.icon;
              return (
                <div key={x.t} data-testid={`mission-card-${x.t.toLowerCase()}`} className="bg-[var(--brand-cream)] p-8">
                  <div className="w-12 h-12 bg-black flex items-center justify-center brand-red mb-5">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display font-black uppercase text-2xl text-black">{x.t}</h3>
                  <p className="mt-3 text-zinc-600 leading-relaxed text-sm md:text-base">{x.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Our History ---------------- */
const HISTORY = [
  "RE//FORM PLACE CHI is a Chicago-based nonprofit faith and community organization committed to spiritual formation, community development, leadership cultivation, and whole-person transformation.",
  "Originally established as Pivot Place CHI, the organization entered a significant period of growth, vision development, and organizational expansion that led to its transition into RE//FORM PLACE CHI. The name RE//FORM reflects our commitment to creating environments where individuals, families, and communities can experience meaningful and sustainable transformation.",
  "Under the leadership of Phillip and Chantel Varnado, RE//FORM has grown into a multifaceted organization serving children, youth, young adults, adults, families, and emerging leaders throughout Chicago and beyond. Our work extends beyond weekly faith gatherings and includes youth development, mentorship, leadership training, emotional wellness, financial literacy, community outreach, grief support, prevention education, and personal and professional development initiatives.",
  "RE//FORM is built on the belief that stronger communities are developed by investing in healthier, equipped, and connected people. Through educational programming, community initiatives, conferences, mentorship opportunities, and strategic partnerships, we work to remove barriers, expand access to resources, strengthen families, and create pathways for individuals to thrive.",
  "As a recognized 501(‌c)(3) organization, RE//FORM PLACE CHI continues to expand its organizational capacity and community partnerships while developing programs designed to meet both the spiritual and practical needs of the communities we serve.",
];

const MILESTONES = [
  { tag: "Beginning", t: "Pivot Place CHI", d: "Founded as a Chicago faith community focused on people, purpose, and presence." },
  { tag: "Growth", t: "Vision Expansion", d: "A season of growth, vision development, and organizational expansion." },
  { tag: "Transition", t: "RE//FORM PLACE CHI", d: "A new name reflecting our commitment to sustainable transformation." },
  { tag: "Today", t: "501(‌c)(3) Organization", d: "Expanding capacity, partnerships, and programs across Chicago and beyond." },
];

export function History() {
  return (
    <section id="history" data-testid="history-section" className="section bg-[var(--bg-primary)]">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-3">Our History</p>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
              From Pivot <br /> To <span className="brand-red">RE//FORM</span>
            </h2>
            <ol className="mt-10 list-none p-0 border-l border-[#27272A]">
              {MILESTONES.map((m, i) => (
                <li key={m.t} data-testid={`milestone-${i}`} className="relative pl-6 pb-8 last:pb-0">
                  <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 bg-[var(--brand-red)]" />
                  <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[var(--brand-yellow)]">{m.tag}</div>
                  <div className="font-display font-black uppercase text-lg text-white mt-1">{m.t}</div>
                  <p className="text-sm text-white/50 mt-1 leading-relaxed">{m.d}</p>
                </li>
              ))}
            </ol>
          </div>
          <div className="lg:col-span-8 space-y-6 text-base md:text-lg leading-relaxed text-white/70" data-testid="history-copy">
            {HISTORY.map((p, i) => (
              <p key={i} className={i === 0 ? "text-white text-lg md:text-xl font-medium" : ""}>
                {p}
              </p>
            ))}
            <p className="pt-4 font-display font-bold uppercase text-xl md:text-2xl leading-tight text-white border-t border-[#27272A]">
              Our commitment is simple: to create{" "}
              <span className="brand-red">A Safe Place to Become. A Strong Place to Belong.</span>{" "}
              while reaching people, developing leaders, strengthening communities, and helping
              individuals discover and pursue their potential.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- What We Do (program areas) ---------------- */
const PROGRAMS = [
  { icon: Sprout, t: "Youth Development", d: "Safe, structured environments where children and teens grow in identity, character, and faith." },
  { icon: Users, t: "Mentorship", d: "One-on-one and group mentoring connecting emerging leaders with seasoned guides." },
  { icon: GraduationCap, t: "Leadership Training", d: "The Classroom, conferences, and cohorts that equip people to lead at home, work, and church." },
  { icon: HeartPulse, t: "Emotional Wellness & Grief Support", d: "Care groups and pastoral support for healing, loss, and whole-person health." },
  { icon: Wallet, t: "Financial Literacy", d: "Practical teaching on budgeting, stewardship, and building generational stability." },
  { icon: HandHeart, t: "Community Outreach", d: "GO Teams, food and supply distribution, holiday drives, and neighborhood evangelism." },
  { icon: ShieldCheck, t: "Prevention Education", d: "Education initiatives that address risk factors and protect families. RE//FORM PLACE CHI is a Certified Recovery Church through the National Center for Community Engagement / Faith & Recovery Coalition.", badge: "Certified Recovery Church" },
  { icon: Briefcase, t: "Personal & Professional Development", d: "WCEP — our Workforce + Community Engagement Program — and career readiness pathways." },
];

export function Programs() {
  return (
    <section id="programs" data-testid="programs-section" className="section bg-[var(--bg-secondary)]">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="eyebrow mb-3">What We Do</p>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
              Programs <span className="brand-red">//</span> Activities
            </h2>
            <p className="mt-4 text-white/60 leading-relaxed">
              Our work extends beyond weekly gatherings. These are the areas where
              RE//FORM invests in people every week.
            </p>
          </div>
          <Link to="/outreach" className="btn-outline-sharp" data-testid="programs-cta-impact">
            See Our Impact <ArrowRight size={14} className="ml-2 inline" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A]">
          {PROGRAMS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.t} data-testid={`program-${i}`} className="bg-[var(--bg-secondary)] p-8 border-l-2 border-transparent hover:border-l-[var(--brand-red)] hover:bg-white/[0.02] transition-colors">
                <span className="w-11 h-11 grid place-items-center bg-[var(--brand-red)]/15 brand-red mb-6">
                  <Icon size={18} />
                </span>
                <h3 className="font-display font-black uppercase text-lg leading-tight text-white">{p.t}</h3>
                {p.badge && (
                  <span
                    data-testid="recovery-church-badge"
                    className="inline-block mt-3 px-2.5 py-1 border border-[var(--brand-yellow)]/40 bg-[var(--brand-yellow)]/[0.08] text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--brand-yellow)]"
                  >
                    {p.badge}
                  </span>
                )}
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{p.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Home: explore the site ---------------- */
const TILES = [
  { to: "/about", t: "About Us", d: "Mission, history, pillars & leadership", img: "/outreach/story-mural-group.webp" },
  { to: "/ministries", t: "Ministries", d: "Men, women, youth, kids & young adults", img: "/outreach/shereforms-2.webp" },
  { to: "/outreach", t: "Outreach", d: "GO Teams, WCEP & 2025 impact", img: "/outreach/englewood-serving.jpg" },
  { to: "/watch", t: "Watch", d: "Livestream Sundays 12:30 PM CST", img: "/media/worship.poster.jpg" },
  { to: "/events", t: "Events", d: "Fire Nights, Classroom & more", img: "/outreach/youth-group.webp" },
  { to: "/give", t: "Give", d: "Partner with the vision", img: "/outreach/serving-together.webp" },
];

export function ExploreTiles() {
  return (
    <section id="explore" data-testid="explore-section" className="section bg-[var(--bg-primary)]">
      <div className="container-x">
        <div className="max-w-2xl mb-12">
          <p className="eyebrow mb-3">Explore RE//FORM</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
            Find Your <span className="brand-red">Place</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#27272A]">
          {TILES.map((x, i) => (
            <Link
              key={x.to}
              to={x.to}
              data-testid={`explore-tile-${x.t.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative bg-black overflow-hidden aspect-[4/3] block"
            >
              <img src={x.img} alt={x.t} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-[1.03] transition-[opacity,transform] duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-7 flex items-end justify-between gap-4">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[var(--brand-yellow)] mb-2">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display font-black uppercase text-2xl md:text-3xl leading-none text-white">{x.t}</h3>
                  <p className="mt-2 text-sm text-white/60">{x.d}</p>
                </div>
                <span className="w-11 h-11 min-w-11 grid place-items-center bg-[var(--brand-red)] text-white translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[opacity,transform]">
                  <ArrowRight size={18} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Generic CTA band ---------------- */
export function CTABand({ eyebrow = "Next Step", title, text, primary, secondary }) {
  return (
    <section data-testid="cta-band" className="bg-[var(--brand-red)] py-16 md:py-20">
      <div className="container-x flex flex-col md:flex-row md:items-center md:justify-between gap-8">
        <div className="max-w-2xl">
          <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-black/60 mb-3">{eyebrow}</p>
          <h2 className="font-display font-black uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight text-white">{title}</h2>
          {text && <p className="mt-4 text-white/85 leading-relaxed">{text}</p>}
        </div>
        <div className="flex flex-wrap gap-3">
          {primary && (
            <Link to={primary.to} className="btn-dark-sharp" data-testid="cta-band-primary">
              {primary.label}
            </Link>
          )}
          {secondary && (
            <Link to={secondary.to} className="btn-outline-sharp" data-testid="cta-band-secondary">
              {secondary.label}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

export { ORG };
